import { useEffect, useRef, useState } from "react";
import {
  Activity,
  BookOpenText,
  ChartColumnIncreasing,
  Check,
  CircleDollarSign,
  CodeXml,
  Database,
  House,
  MessageSquare,
  Newspaper,
  NotebookText,
  Scale,
  Settings,
  Shield,
  SquareScissors,
  Ticket,
  WandSparkles,
} from "lucide-react";

import { NavMain } from "@/components/side-bar/nav-main";
import {
  NavSecondary,
  type NavSecondaryItem,
} from "@/components/side-bar/nav-secondary";
import { NavUser } from "@/components/side-bar/nav-user";
import { OrgSwitcher } from "@/components/side-bar/org-switcher";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from "@/components/ui/sidebar";

import { useIntercom } from "react-use-intercom";

import {
  isAdminIdentity,
  isSalesIdentity,
  useIdentity,
} from "contexts/identity-context";
import { useIntercomUnread } from "contexts/intercom-unread-context";
import * as Routes from "routes";

// Footer links shown above the chat trigger.
const navSecondaryTop: NavSecondaryItem[] = [
  {
    name: "Settings",
    url: Routes.userGeneralSettings,
    activePrefix: "/settings",
    icon: Settings,
  },
  { name: "What's New", url: Routes.whatsNew, icon: Newspaper },
  {
    name: "System Status",
    url: "https://status.geoscape.com.au",
    icon: Activity,
    target: "_blank",
  },
  {
    name: "Raise a Ticket",
    url: "https://support.geoscape.com.au/",
    icon: Ticket,
    target: "_blank",
  },
];

// Legal is pinned below the chat trigger, at the bottom of the footer links.
const navSecondaryLegal: NavSecondaryItem = {
  name: "Legal",
  url: Routes.legal,
  icon: Scale,
};

export function AppSidebar(props: React.ComponentProps<typeof Sidebar>) {
  const [identityState] = useIdentity();
  const { show } = useIntercom();
  const unreadCount = useIntercomUnread();
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(
    null,
  );
  const navigationRef = useRef<HTMLDivElement>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  useEffect(() => {
    if (!scrollContainer) return;

    const updateScrollShadow = () => {
      setCanScrollUp(scrollContainer.scrollTop > 1);
      setCanScrollDown(
        scrollContainer.scrollHeight -
          scrollContainer.clientHeight -
          scrollContainer.scrollTop >
          1,
      );
    };

    updateScrollShadow();
    const observer = new ResizeObserver(updateScrollShadow);
    observer.observe(scrollContainer);
    // Expanded menus can change the scroll height without resizing the viewport.
    if (navigationRef.current) observer.observe(navigationRef.current);
    scrollContainer.addEventListener("scroll", updateScrollShadow, {
      passive: true,
    });

    return () => {
      observer.disconnect();
      scrollContainer.removeEventListener("scroll", updateScrollShadow);
    };
  }, [scrollContainer]);

  const navGroups = [
    {
      items: [
        { title: "Home", url: Routes.hub, icon: House },
        {
          title: "Data Catalogue",
          url: Routes.dataCatalogue,
          icon: BookOpenText,
        },
        {
          title: "Dataset Downloads",
          url: "/datasets",
          icon: Database,
          items: [
            { title: "Standard", url: Routes.dataDownload },
            { title: "My Custom", url: Routes.dataCustomDownload },
          ],
        },
      ],
    },
    {
      label: "Products",
      items: [
        {
          title: "Geoscape Verify",
          url: Routes.verify,
          icon: Check,
          items: [
            { title: "Geocoder Playground", url: Routes.verifyGeocoder },
            { title: "Verify Studio", url: Routes.verifyStudio },
          ],
        },
        {
          title: "Geoscape Clip",
          url: Routes.clip,
          icon: SquareScissors,
          items: [
            { title: "Explorer", url: Routes.dataExplorer },
            { title: "My Clips", url: Routes.clipManagement },
          ],
        },
        {
          title: "Interactive Demos",
          url: Routes.demosPage,
          icon: WandSparkles,
        },
      ],
    },
    {
      label: "Develop",
      items: [
        { title: "My Apps", url: Routes.developerApiKeys, icon: CodeXml },
        {
          title: "Usage",
          url: Routes.developerUsage,
          icon: ChartColumnIncreasing,
        },
        { title: "Docs", url: Routes.docsPage, icon: NotebookText },
      ],
    },
    {
      label: "Internal",
      items: [
        ...(isAdminIdentity(identityState)
          ? [
              {
                title: "Admin",
                url: "/admin",
                icon: Shield,
                items: [
                  { title: "Organisations", url: Routes.adminOrganisations },
                  { title: "Demos", url: Routes.adminDemoManagement },
                  { title: "Users", url: Routes.adminUsers },
                  { title: "Datasets", url: Routes.adminDatasets },
                  {
                    title: "Custom Datasets",
                    url: Routes.adminViewCustomDatasets,
                  },
                  { title: "Releases", url: Routes.adminReleases },
                  { title: "Clip Reporting", url: Routes.adminClipReporting },
                  {
                    title: "Email Reporting",
                    url: Routes.adminEmailReporting,
                  },
                ],
              },
            ]
          : []),
        ...(isSalesIdentity(identityState)
          ? [
              {
                title: "Sales Tools",
                url: "/sales-tools",
                icon: CircleDollarSign,
                items: [
                  {
                    title: "Building Updates",
                    url: Routes.salesToolsRuralSchedule,
                  },
                  { title: "Opportunities", url: Routes.salesToolsOpportunities },
                ],
              },
            ]
          : []),
      ],
    },
  ].filter((group) => group.items.length > 0);

  const chatItem: NavSecondaryItem = {
    name: "Chat with Our Team",
    icon: MessageSquare,
    onClick: () => show(),
    badgeCount: unreadCount,
  };

  // Order: top links, then the chat trigger, then Legal pinned at the bottom.
  const navSecondaryItems: NavSecondaryItem[] = [
    ...navSecondaryTop,
    chatItem,
    navSecondaryLegal,
  ];

  return (
    <Sidebar collapsible="icon" variant="sidebar" {...props}>
      <SidebarHeader className="relative shrink-0">
        <OrgSwitcher />
        {canScrollUp && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-full z-10 h-4 bg-gradient-to-b from-sidebar-foreground/15 to-transparent"
          />
        )}
      </SidebarHeader>
      <SidebarContent ref={setScrollContainer}>
        <div ref={navigationRef} className="shrink-0">
          <NavMain groups={navGroups} />
        </div>
      </SidebarContent>
      <SidebarFooter className="relative shrink-0">
        {canScrollDown && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-full h-4 bg-gradient-to-t from-sidebar-foreground/15 to-transparent"
          />
        )}
        <NavSecondary items={navSecondaryItems} />
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
